import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { VenueAgentDto } from "../../../models/court-recording/dto/venue-agent.dto";

export const GET_VENUE_AGENTS_QUERY_KEY = (organizationId: string) =>
    ['court-recordings', 'venue-agents', 'list', organizationId];

/**
 * `refetchIntervalMs` lets the camera setup page poll faster than the fleet
 * list: an installer who just pressed "take a photo" is waiting on the box's
 * next heartbeat (~60 s), and a 60 s list poll on top of that reads as "it
 * did nothing". Same query key, so both pages share one cache entry — the
 * shortest active interval wins while both are mounted.
 */
export const useListVenueAgents = (
    organizationId: string,
    options: { refetchIntervalMs?: number } = {},
) => {
    const client = useApiClient();

    return useQuery({
        queryKey: GET_VENUE_AGENTS_QUERY_KEY(organizationId),
        queryFn: () =>
            client.fetchApi<VenueAgentDto[], {}>({
                endpoint: `court-recordings/admin/venue-agents?organizationId=${organizationId}`,
                method: 'GET',
            }),
        // Heartbeat updates every poll cycle (~60s). 30s stale window keeps
        // the operator's "is the agent up?" picture roughly fresh without
        // hammering the API.
        staleTime: 30_000,
        refetchInterval: options.refetchIntervalMs ?? 60_000,
        enabled: Boolean(organizationId),
    });
};
